import { Disclosure } from '@headlessui/react';
import { useFormikContext } from 'formik';
import { memo, useMemo } from 'react';
import { Fragment } from 'react/jsx-runtime';

import { DisclosureBody, DisclosureModal, FormField } from '@components';
import { InputType } from '@enums';
import { validateValueEqual } from '@utils';

interface GFormFieldsProps {
  inputList?: any[];
  touched?: Record<string, any>;
  errors?: Record<string, any>;
}

export const GFormFields = memo(({ inputList = [], touched = {}, errors = {} }: GFormFieldsProps) => {
  const { values } = useFormikContext<Record<string, any>>();

  const { regularFields, collapsibleGroups } = useMemo(() => {
    const regular: any[] = [];
    const groups: Record<string, any[]> = {};

    inputList.forEach((field) => {
      if (field.collapsible && field.collapsibleGroup) {
        if (!groups[field.collapsibleGroup]) {
          groups[field.collapsibleGroup] = [];
        }
        groups[field.collapsibleGroup].push(field);
      } else {
        regular.push(field);
      }
    });

    return { regularFields: regular, collapsibleGroups: groups };
  }, [inputList]);

  const renderFormField = (inputForm: any, index: number) => {
    const isCustomForm = validateValueEqual(inputForm.inputType, InputType.Custom);

    // Handle conditional field hiding
    if (inputForm.hide) {
      const { field, condition } = inputForm.hide;
      const watchedValue = values?.[field];
      if (condition(watchedValue)) {
        return null;
      }
    }

    if (isCustomForm && inputForm.children) {
      return inputForm.children;
    }

    const { isCorrect, name, label, withoutLabel, ...props } = inputForm;

    return (
      <FormField
        label={label}
        name={name}
        withoutLabel={!label || withoutLabel}
        isCorrect={!!isCorrect && touched[name] && !errors[name]}
        fullWidth
        {...props}
      />
    );
  };

  const renderCollapsibleGroup = (groupName: string, fields: any[]) => {
    const titleField = fields.find((field) => field.collapsibleTitle);
    const title = titleField?.collapsibleTitle || 'Advanced Options';

    const hasErrors = fields.some((field) => errors?.[field.name]);
    const hasValues = fields.some((field) => values?.[field.name] && values[field.name] !== field.defaultValue);

    return (
      <div key={groupName} className="mt-3">
        <Disclosure>
          <DisclosureModal
            button={{
              label: title,
              defaultOpen: hasErrors || hasValues,
            }}
          >
            <DisclosureBody notForm>
              {fields.map((field, index) => (
                <Fragment key={`${field.name}-${index}`}>{renderFormField(field, index)}</Fragment>
              ))}
            </DisclosureBody>
          </DisclosureModal>
        </Disclosure>
      </div>
    );
  };

  return (
    <>
      {regularFields.map((inputForm, index) => (
        <Fragment key={`${inputForm.name}-${index}`}>{renderFormField(inputForm, index)}</Fragment>
      ))}

      {Object.entries(collapsibleGroups).map(([groupName, fields]) => renderCollapsibleGroup(groupName, fields))}
    </>
  );
});
